<script lang="ts">
  import {
    PUBLIC_APPLE_CLIENT_ID,
    PUBLIC_GOOGLE_CLIENT_ID,
  } from '$env/static/public'
  import type { GoogleCallback } from '$lib/types/googleCallback'
  import { Button, CircularProgress } from '@bruce/components'
  import { onMount } from 'svelte'
  import { utils } from '$lib/utils'
  import { goto } from '$app/navigation'
  import { page } from '$app/stores'
  import type { SocialContext } from '$lib/types/socialCallbackState'
  import { browser } from '$app/environment'

  export let social: 'apple' | 'google' = 'apple'
  export let context: SocialContext
  export let state: string | undefined = undefined

  const appleScript =
    'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'
  const googleScript = 'https://accounts.google.com/gsi/client'
  let mounted = false

  let appleLoaded =
    typeof window !== 'undefined' &&
    'AppleID' in window &&
    'auth' in window.AppleID

  let googleLoaded =
    typeof window !== 'undefined' &&
    'google' in window &&
    'accounts' in window.google

  let googleLoading = false
  $: googleSignInIdToken = $page.url.searchParams.get('id_token')

  $: if (googleSignInIdToken && browser) {
    handleGoogleCallback(googleSignInIdToken)
  }

  onMount(() => {
    mounted = true

    return () => {
      mounted = false
    }
  })

  function handleClick() {
    if (social === 'apple') {
      handleAppleAuth()
    } else if (social === 'google') {
      handleGoogleAuth()
    }
  }

  async function handleAppleAuth() {
    try {
      window.AppleID.auth.init({
        clientId: PUBLIC_APPLE_CLIENT_ID,
        scope: 'name email',
        redirectURI: `${window.location.origin}/callback/apple`,
        state,
      })

      await window.AppleID.auth.signIn()
    } catch (error) {
      console.error(
        `${utils.toTitleCase(context)} with Apple ID failed with error: `,
        error,
      )
    }
  }

  async function handleGoogleAuth() {
    googleLoading = true
    google.accounts.id.initialize({
      client_id: `${PUBLIC_GOOGLE_CLIENT_ID}.apps.googleusercontent.com`,
      callback: handleGoogleCallback,
      context,
      use_fedcm_for_prompt: true,
    })

    google.accounts.id.prompt((notification) => {
      if (notification.isSkippedMoment()) {
        redirectToGoogleSignIn()
      }
    })
  }

  async function handleGoogleCallback(
    response: google.accounts.id.CredentialResponse | string,
  ) {
    const body: GoogleCallback =
      typeof response === 'string'
        ? { google_jwt: response, state }
        : { google_jwt: response.credential, state }

    googleLoading = true

    try {
      const response = await fetch('/callback/google', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
        },
        body: JSON.stringify(body),
      })

      if (response.redirected) {
        await goto(response.url)
        googleLoading = false
      }
    } catch (e) {
      googleLoading = false
      console.error(
        `${utils.toTitleCase(context)} with Google failed with error: `,
        e,
      )
    }
  }

  function redirectToGoogleSignIn() {
    const googleSignInUrl = new URL(
      'https://accounts.google.com/o/oauth2/v2/auth',
    )

    googleSignInUrl.searchParams.append('client_id', PUBLIC_GOOGLE_CLIENT_ID)
    googleSignInUrl.searchParams.append('response_type', 'code')
    googleSignInUrl.searchParams.append('scope', 'profile email')

    googleSignInUrl.searchParams.append(
      'redirect_uri',
      `${window.location.origin}/callback/google`,
    )

    if (state) {
      googleSignInUrl.searchParams.append('state', state)
    }

    window.location.href = googleSignInUrl.href
    googleLoading = false
  }
</script>

<svelte:head>
  {#if social === 'apple' && mounted && !appleLoaded}
    <script
      async
      src={appleScript}
      on:load={() => (appleLoaded = true)}
    ></script>
  {/if}
  {#if social === 'google' && mounted && !googleLoaded}
    <script
      async
      src={googleScript}
      on:load={() => (googleLoaded = true)}
    ></script>
  {/if}
</svelte:head>
<div class={social} class:googleLoading>
  {#if social === 'google' && googleLoading}
    <CircularProgress indeterminate />
  {:else}
    <Button icon={social} variant="flat" type="button" on:click={handleClick} />
  {/if}
</div>

<style lang="scss">
  div {
    &.apple {
      --button-color: #000;
      --button-icon-color: #fff;
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 8px;
    }

    &.google {
      --button-color: #fff;
      border: 1px solid rgba(0, 0, 0, 0.1);
      border-radius: 8px;
      display: flex;
      align-items: center;

      &.googleLoading {
        border: none;
      }
    }
  }
</style>
